import type { MutableRefObject } from 'react';
import { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { USER_SAM_ON_BOARDING_COMPLETED } from '../../../../domain/user';
import { invalidateAllInsightsQueries } from '../../../../features/insights/useInsightsQuery';
import { useUpdateCountriesMutation } from '../../../../features/preferences/useCountryPreferencesMutation';
import { useUpdateMetricsMutation } from '../../../../features/preferences/useMetricsPreferencesMutation';
import { useSuggestedMetricsQuery } from '../../../../features/preferences/useSuggestedMetricsQuery';
import { useUpdateValuePropositionMutation } from '../../../../features/preferences/useValuePropPreferencesMutation';
import { useValuePropPreferencesQuery } from '../../../../features/preferences/useValuePropPreferencesQuery';
import { markOnboardingAsCompleted } from '../../../../features/user/markOnboardingAsCompleted';
import { useUserSettingsMutation } from '../../../../features/user/useUserSettingsMutation';
import { useAnimateAppearance } from '../../../../hooks/useAnimateAppearance';
import { animateAppearance } from '../../../../services/animations';
import { useDispatchTrackEventWithLocation } from '../../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { LoadingView } from '../../LoadingView';
import type { LoadingViewRefObject } from '../../LoadingView/LoadingView';

import { OnboardingStep } from './Onboarding';
import { StepHeader } from './StepHeader';
import { StepInput } from './StepInput';
import { StyledContinueButton, StyledOnboardingContent, StyledOnboardingOverlay, StyledStepsLeft } from './styles';
import { useOnboardingSteps } from './useOnboardingSteps';
import { useSamSettings } from './useSamSettings';
import { useStepTexts } from './useStepTexts';

type OnboardingOverlayProps = {
  onClose(): void;
};

export const OnboardingOverlay = forwardRef<HTMLDivElement, OnboardingOverlayProps>((props, ref) => {
  const dispatchTrackEvent = useDispatchTrackEventWithLocation();
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const loadingViewRef = useRef<LoadingViewRefObject>(null);
  const [isLoadingDisappearing, setIsLoadingDisappearing] = useState(false);
  const { isAnimating, appear, disappear } = useAnimateAppearance();

  const vpAndPpMutation = useUpdateValuePropositionMutation();
  const { isLoading: isLoadingVpAndPp, data: initialVpAndPp } = useValuePropPreferencesQuery();

  const [settings, setSettings] = useSamSettings();

  const suggestedMetricsQuery = useSuggestedMetricsQuery({
    enabled: false,
    onSuccess: (initialMetrics) => setSettings((prev) => ({ ...prev, metrics: initialMetrics })),
    onSettled: () => {
      setIsLoadingDisappearing(true);
      loadingViewRef.current
        ?.disappear()
        .then(() => setIsLoadingDisappearing(false))
        .catch(console.error);
    },
  });
  const updateMetricsMutation = useUpdateMetricsMutation();
  const updateCountriesMutation = useUpdateCountriesMutation();
  const finishOnboardingMutation = useUserSettingsMutation();

  const { step, goToNextStep, goToPreviousStep, isLastStep, isThirdStepOfFour, stepsCount, stepIndex } =
    useOnboardingSteps();

  const handleGoBack = () => {
    const pageName = {
      [OnboardingStep.painPoint]: 'PP page',
      [OnboardingStep.valueProposition]: 'VP page',
      [OnboardingStep.metrics]: 'metrics page',
      [OnboardingStep.countries]: 'country selection page',
    }[step];
    dispatchTrackEvent('preferences', 'click go back', pageName);
    goToPreviousStep();
  };

  const handleClose = () => {
    dispatchTrackEvent('preferences', 'click close', 'close onboarding');
    props.onClose();
  };

  const { title, subtitle } = useStepTexts(step);

  const value = settings[step];

  const finishOnboarding = () => {
    const subName = step === OnboardingStep.countries ? 'country selection page' : 'metric selection page';
    dispatchTrackEvent('preferences', 'click finish setup', `finish onboarding setup/${subName}`);
    invalidateAllInsightsQueries();
    finishOnboardingMutation.mutate({ [USER_SAM_ON_BOARDING_COMPLETED]: true });
    const container = (ref as MutableRefObject<HTMLDivElement>)?.current ?? null;
    animateAppearance(container, 'disappear')
      ?.finished.then(() => markOnboardingAsCompleted())
      .catch(console.error);
  };

  const getPpAndVpStatus = (initialValue: string, value: string) => {
    if (!initialValue) {
      return 'created new';
    }
    return value === initialValue ? `didn't make changes` : 'edited existing';
  };

  const handleContinue = () => {
    if (step === OnboardingStep.painPoint) {
      goToNextStep();
      const subName = getPpAndVpStatus(initialVpAndPp?.default.painPoint ?? '', settings.painPoint);
      dispatchTrackEvent('preferences', 'click continue', `approve PP/${subName}`);
    }

    if (step === OnboardingStep.valueProposition) {
      const subName = getPpAndVpStatus(initialVpAndPp?.default.valueProposition ?? '', settings.valueProposition);
      dispatchTrackEvent('preferences', 'click continue', `approve VP/${subName}`);
      const contentEl = contentRef.current;
      if (!contentEl) {
        goToNextStep();
        return;
      }

      disappear(contentEl)
        .then(() => {
          goToNextStep();
          const payload = { valueProposition: settings.valueProposition, painPoint: settings.painPoint };
          vpAndPpMutation.mutate(payload, {
            onSettled: () => void suggestedMetricsQuery.refetch(),
          });
        })
        .catch(console.error);
    }

    if (step === OnboardingStep.metrics) {
      dispatchTrackEvent('preferences', 'click continue', `metric selection completed/${settings.metrics.length}`);
      updateMetricsMutation.mutate(settings.metrics, {
        onSettled: () => {
          if (isLastStep) {
            finishOnboarding();
            return;
          }

          const contentEl = contentRef.current;
          if (!contentEl) {
            goToNextStep();
            return;
          }

          disappear(contentEl)
            .then(() => goToNextStep())
            .then(() => appear(contentEl))
            .catch(console.error);
        },
      });
    }

    if (step === OnboardingStep.countries) {
      updateCountriesMutation.mutate(settings.countries, { onSettled: finishOnboarding });
    }
  };

  const isEmpty = () => value === undefined || (typeof value === 'string' ? !value.trim() : value.length === 0);

  const isLoadingMetrics =
    step === OnboardingStep.metrics &&
    (suggestedMetricsQuery.isFetching ||
      !suggestedMetricsQuery.isFetched ||
      vpAndPpMutation.isPending ||
      isLoadingDisappearing);

  const isContinueDisabled =
    isEmpty() || updateMetricsMutation.isPending || updateCountriesMutation.isPending || isAnimating;

  useLayoutEffect(() => {
    const contentEl = contentRef.current;
    if (isLoadingMetrics || !contentEl) {
      return;
    }

    appear(contentEl).catch(console.error);
  }, [isLoadingMetrics]);

  return (
    <StyledOnboardingOverlay ref={ref}>
      {isLoadingMetrics ? (
        <LoadingView variant="onboarding" ref={loadingViewRef} />
      ) : (
        <StyledOnboardingContent ref={contentRef}>
          <StepHeader
            stepsCount={stepsCount}
            stepIndex={stepIndex}
            onBack={handleGoBack}
            onClose={handleClose}
            title={title}
            subtitle={subtitle}
          />

          <StepInput step={step} settings={settings} onChange={setSettings} isLoadingVpAndPp={isLoadingVpAndPp} />
          <StyledContinueButton
            disabled={isContinueDisabled}
            onClick={handleContinue}
            isLoading={updateMetricsMutation.isPending || updateCountriesMutation.isPending}
            smallOffset={step === OnboardingStep.metrics}
          >
            {isLastStep ? t('insights.onboarding.finish_setup') : t('common.continue')}
          </StyledContinueButton>
          {isThirdStepOfFour ? <StyledStepsLeft>{t('insights.onboarding.one_step')}</StyledStepsLeft> : null}
        </StyledOnboardingContent>
      )}
    </StyledOnboardingOverlay>
  );
});
